import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchTop10 } from '@/lib/api';
import { getGradeColor, getSectorColor } from '@/lib/constants';
import { GitCompareArrows, Medal, Trophy, TrendingUp } from 'lucide-react';

const PODIUM_COLORS = ['#D8B45A', '#BFC7D5', '#B37A52'];
const PODIUM_LABELS = ['1st', '2nd', '3rd'];
const PODIUM_HEIGHTS = ['h-52 md:h-60', 'h-44 md:h-52', 'h-40 md:h-48'];
const PODIUM_ORDER = [1, 0, 2];

export default function Top10Podium({ onCompanyClick, compareIds, onCompareToggle, selectedCountry, refreshTick }) {
  const [category, setCategory] = useState('Overall');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const categoryParam = category === 'Overall' ? null : category;
    fetchTop10(categoryParam, selectedCountry || undefined, true)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category, selectedCountry, refreshTick]);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <div className="flex items-end justify-center gap-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className={`w-full max-w-[250px] ${PODIUM_HEIGHTS[index]} rounded-t-3xl border border-white/10 bg-white/[0.04] animate-pulse`} />
          ))}
        </div>
      </div>
    );
  }

  const podium = data.podium || [];
  const list = data.list || [];

  return (
    <section className="space-y-6" data-testid="top10-section">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs uppercase tracking-[0.18em] text-white/35">Top 10</p>
            <Badge className="border border-white/10 bg-white/5 text-[#D8B45A]">
              {selectedCountry || 'Global benchmark'}
            </Badge>
          </div>
          <h2 className="mt-2 text-2xl font-bold text-white">Premium sustainability podium</h2>
          <p className="mt-2 text-sm text-white/45">The most competitive ESG leaders in the current Formula 1 market context, refreshed from real country-level coverage.</p>
        </div>

        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="rounded-2xl border border-white/10 bg-white/5 p-1">
            {['Overall', 'Financial', 'Energy', 'Corporate'].map((value) => (
              <TabsTrigger key={value} value={value} className="rounded-xl px-4 py-2 text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/55">
                {value}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {podium.length >= 3 ? (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-end md:justify-center" data-testid="top10-podium">
          {PODIUM_ORDER.map((visualIndex) => {
            const company = podium[visualIndex];
            if (!company) return null;
            const gradeColor = getGradeColor(company.grade);
            const podiumColor = PODIUM_COLORS[visualIndex];

            return (
              <motion.div
                key={company.id}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: visualIndex * 0.1, duration: 0.35 }}
                className={`relative w-full max-w-[280px] ${PODIUM_HEIGHTS[visualIndex]} cursor-pointer`}
                data-testid={visualIndex === 0 ? 'top10-podium-first' : undefined}
                onClick={() => onCompanyClick(company)}
              >
                <div className="absolute inset-0 rounded-t-3xl border border-white/10 bg-[rgba(13,34,56,0.92)] shadow-[0_12px_32px_rgba(0,0,0,0.32)]" style={{ borderTopWidth: '4px', borderTopColor: podiumColor }}>
                  <div className="flex h-full flex-col justify-between p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {visualIndex === 0 ? <Trophy className="h-5 w-5" style={{ color: podiumColor }} /> : <Medal className="h-5 w-5" style={{ color: podiumColor }} />}
                        <span className="font-mono text-sm font-semibold" style={{ color: podiumColor }}>{PODIUM_LABELS[visualIndex]}</span>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          onCompareToggle(company.id);
                        }}
                        className={`h-8 w-8 rounded-full ${compareIds.includes(company.id) ? 'bg-[#2A9D8F]/15 text-[#D8FBF7]' : 'text-white/40 hover:bg-white/[0.08] hover:text-white'}`}
                      >
                        <GitCompareArrows className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="text-center">
                      <h3 className="truncate text-base font-semibold text-white">{company.name}</h3>
                      <p className="mt-2 font-mono text-4xl font-bold tabular-nums" style={{ color: gradeColor }}>{company.overall_score}</p>
                      <Badge className="mt-2 border border-white/10" style={{ backgroundColor: `${gradeColor}24`, color: gradeColor }}>
                        {company.grade}
                      </Badge>
                    </div>

                    <div>
                      <Badge variant="outline" className="border-white/10 text-[10px]" style={{ color: getSectorColor(company.sector), borderColor: `${getSectorColor(company.sector)}40` }}>
                        {company.sector} &middot; {company.gp_country}
                      </Badge>
                      <p className="mt-2 text-xs text-white/40 line-clamp-2">{company.key_strengths?.[0] || company.coverage_note}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : null}

      <div className="space-y-2" data-testid="top10-list">
        {list.map((company, index) => {
          const gradeColor = getGradeColor(company.grade);
          return (
            <motion.div
              key={company.id}
              role="button"
              tabIndex={0}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03, duration: 0.24 }}
              onClick={() => onCompanyClick(company)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onCompanyClick(company);
              }}
              className="flex w-full cursor-pointer items-center gap-4 rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.9)] px-4 py-4 text-left shadow-[0_10px_30px_rgba(0,0,0,0.28)] transition-colors duration-150 hover:border-white/20"
            >
              <span className="w-10 text-center font-mono text-lg font-bold text-white/30 tabular-nums">#{index + 4}</span>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-sm font-semibold text-white">{company.name}</h4>
                <p className="text-xs text-white/40">{company.gp_country} &middot; {company.sector} &middot; {company.sub_sector}</p>
              </div>
              <div className="hidden items-center gap-1 rounded-full bg-white/[0.04] px-2.5 py-1 text-xs text-[#4ECDC4] md:flex">
                <TrendingUp className="h-3.5 w-3.5" />
                <span className="font-mono tabular-nums">{company.improvement_pct > 0 ? '+' : ''}{company.improvement_pct}%</span>
              </div>
              <span className="font-mono text-2xl font-bold tabular-nums" style={{ color: gradeColor }}>{company.overall_score}</span>
              <Badge className="border border-white/10" style={{ backgroundColor: `${gradeColor}24`, color: gradeColor }}>{company.grade}</Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  onCompareToggle(company.id);
                }}
                data-testid="top10-compare-toggle"
                className={`h-9 w-9 rounded-full ${compareIds.includes(company.id) ? 'bg-[#2A9D8F]/15 text-[#D8FBF7]' : 'text-white/40 hover:bg-white/[0.08] hover:text-white'}`}
              >
                <GitCompareArrows className="h-4 w-4" />
              </Button>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
